import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Sparkles } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { TYPE_LABELS } from "@/data/content";

const QUESTIONS = [
  {
    q: "Was steht bei dir an?",
    options: [
      { label: "Unsere Hochzeit", scores: { hochzeit: 3 } },
      { label: "Eine Feier oder ein Event", scores: { events: 3 } },
      { label: "Nichts Bestimmtes — einfach schöne Bilder", scores: { paare: 1, familie: 1, landschaft: 1 } },
      { label: "Etwas für mein Business", scores: { unternehmen: 3 } },
    ],
  },
  {
    q: "Wer steht vor der Kamera?",
    options: [
      { label: "Wir zwei", scores: { paare: 3, hochzeit: 1 } },
      { label: "Die ganze Familie", scores: { familie: 3 } },
      { label: "Ich — mit Babybauch", scores: { babybauch: 3 } },
      { label: "Mein tierischer Liebling", scores: { tiere: 3 } },
      { label: "Mein Auto", scores: { automotive: 3 } },
    ],
  },
  {
    q: "Welche Stimmung soll es haben?",
    options: [
      { label: "Romantisch & emotional", scores: { hochzeit: 2, paare: 2 } },
      { label: "Ausgelassen & lebendig", scores: { events: 2, familie: 1 } },
      { label: "Ruhig & natürlich", scores: { landschaft: 2, familie: 1, babybauch: 1, tiere: 1 } },
      { label: "Edel & professionell", scores: { unternehmen: 2, automotive: 2 } },
    ],
  },
  {
    q: "Wo fühlst du dich am wohlsten?",
    options: [
      { label: "Draußen in der Natur", scores: { landschaft: 2, familie: 1, tiere: 1, paare: 1 } },
      { label: "Mitten in der Stadt", scores: { automotive: 2, events: 1, unternehmen: 1 } },
      { label: "Ganz entspannt zuhause", scores: { familie: 2, babybauch: 2 } },
      { label: "Überall — Hauptsache gute Bilder", scores: { paare: 1, hochzeit: 1 } },
    ],
  },
];

const RESULT_TEXT = {
  hochzeit: "Der wichtigste Tag verdient Bilder, die sich in 20 Jahren noch genauso anfühlen.",
  paare: "Echte Nähe statt steifer Posen — ein Shooting, das sich wie ein Date anfühlt.",
  familie: "Chaos, Lachen und kleine Hände — genau das macht die schönsten Familienbilder aus.",
  babybauch: "Diese besondere Zeit vergeht so schnell — halten wir sie gemeinsam fest.",
  events: "Ausgelassen, laut, echt — ich halte die Momente fest, während du feierst.",
  unternehmen: "Professionelle Bilder, die deinem Business ein Gesicht geben.",
  tiere: "Mit Geduld und Herz — Portraits deines Vierbeiners, die Charakter zeigen.",
  automotive: "Dein Auto in Szene gesetzt — Details, Linien und das perfekte Licht.",
  landschaft: "Die Schönheit unserer Heimat — ruhig, weit und zeitlos.",
  sonstiges: "Erzähl mir einfach, was du vorhast — wir finden gemeinsam das Passende.",
};

export const StyleQuiz = () => {
  const [step, setStep] = useState(-1); // -1 = Intro, QUESTIONS.length = Ergebnis
  const [scores, setScores] = useState({});
  const [result, setResult] = useState(null);

  const answer = (opt) => {
    const next = { ...scores };
    Object.entries(opt.scores).forEach(([k, v]) => {
      next[k] = (next[k] || 0) + v;
    });
    setScores(next);
    if (step + 1 >= QUESTIONS.length) {
      const best = Object.entries(next).sort((a, b) => b[1] - a[1])[0]?.[0] || "sonstiges";
      setResult(best);
    }
    setStep(step + 1);
  };

  const goBooking = () => {
    window.dispatchEvent(new CustomEvent("leno:shoot-type", { detail: result }));
    scrollToId("#termine");
  };

  return (
    <section id="stil-finder" data-testid="style-quiz-section" className="border-t border-white/10">
      <div className="mx-auto max-w-[900px] px-6 py-24 lg:py-36">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="text-center"
        >
          <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.35em] text-gold">Stil-Finder</p>
          <h2 className="font-serif text-3xl font-light tracking-tight text-white sm:text-4xl lg:text-5xl">
            Welches Shooting <span className="italic text-zinc-400">passt zu dir?</span>
          </h2>
        </motion.div>

        <div className="mt-12 border border-white/10 bg-ink2 p-8 lg:p-14">
          <AnimatePresence mode="wait">
            {step === -1 && (
              <motion.div
                key="intro"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="text-center"
                data-testid="quiz-intro"
              >
                <Sparkles size={26} className="mx-auto text-gold" />
                <p className="mx-auto mt-6 max-w-md text-sm leading-relaxed text-zinc-400">
                  Vier kurze Fragen — und ich sage dir, welche Art von Shooting zu dir passt.
                  Dauert keine 30 Sekunden.
                </p>
                <button
                  onClick={() => setStep(0)}
                  data-testid="quiz-start-button"
                  className="group mt-8 inline-flex items-center gap-3 rounded-full bg-gold px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-ink transition-colors hover:bg-white"
                >
                  Quiz starten
                  <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </button>
              </motion.div>
            )}

            {step >= 0 && step < QUESTIONS.length && (
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`quiz-question-${step}`}
              >
                <div className="mb-8 flex items-center gap-4">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    Frage {step + 1} / {QUESTIONS.length}
                  </span>
                  <div className="h-px flex-1 bg-white/10">
                    <motion.div
                      className="h-px bg-gold"
                      initial={false}
                      animate={{ width: `${((step + 1) / QUESTIONS.length) * 100}%` }}
                      transition={{ duration: 0.5 }}
                      data-testid="quiz-progress"
                    />
                  </div>
                </div>
                <h3 className="font-serif text-2xl font-light text-white sm:text-3xl">{QUESTIONS[step].q}</h3>
                <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {QUESTIONS[step].options.map((opt, i) => (
                    <button
                      key={opt.label}
                      onClick={() => answer(opt)}
                      data-testid={`quiz-option-${step}-${i}`}
                      className="group border border-white/10 bg-ink px-6 py-5 text-left text-sm text-zinc-300 transition-all duration-300 hover:border-gold hover:bg-gold/5 hover:text-white"
                    >
                      <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-gold opacity-0 transition-opacity group-hover:opacity-100">
                        Das bin ich →
                      </span>
                      <span className="mt-1 block">{opt.label}</span>
                    </button>
                  ))}
                </div>
                {step > 0 && (
                  <button
                    onClick={() => setStep(step - 1)}
                    data-testid="quiz-back-button"
                    className="mt-6 flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-600 transition-colors hover:text-gold"
                  >
                    <ArrowLeft size={12} /> Zurück
                  </button>
                )}
              </motion.div>
            )}

            {step >= QUESTIONS.length && result && (
              <motion.div
                key="result"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="text-center"
                data-testid="quiz-result"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-gold">Dein Match</p>
                <h3 className="mt-4 font-serif text-4xl font-light text-white sm:text-5xl" data-testid="quiz-result-title">
                  {TYPE_LABELS[result]}
                </h3>
                <p className="mx-auto mt-5 max-w-md text-sm leading-relaxed text-zinc-400">{RESULT_TEXT[result]}</p>
                <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                  <button
                    onClick={goBooking}
                    data-testid="quiz-booking-button"
                    className="group inline-flex items-center gap-3 rounded-full bg-gold px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-ink transition-colors hover:bg-white"
                  >
                    Direkt Termin anfragen
                    <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                  </button>
                  <button
                    onClick={() => {
                      setStep(-1);
                      setScores({});
                      setResult(null);
                    }}
                    data-testid="quiz-restart-button"
                    className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 transition-colors hover:text-gold"
                  >
                    Noch einmal
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
